import { useEffect, useMemo, useState } from "react";

import { TerritoryLevel } from "@mapmycustomers/shared/enum";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import { AreaMetaFile } from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import { CountryState } from "@mapmycustomers/shared/types/territory/CountryState";

import getBoundaryUrl from "@app/scene/map/utils/getBoundaryUrl";

const useCountryStateDisplayNames = (
  countries: CountryCode[],
  active: boolean = true
): [data: Record<CountryState, string>, loading: boolean, error: undefined | unknown] => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [metaData, setMetaData] = useState<Partial<Record<CountryCode, AreaMetaFile>>>({});

  useEffect(() => {
    if (!active) {
      return;
    }

    setLoading(true);
    (async () => {
      Promise.all(
        countries.map((country) =>
          fetch(getBoundaryUrl("meta", `${country}_${TerritoryLevel.ADM1}` as AreaType))
        )
      )
        .then((responses) => Promise.all(responses.map((response) => response.json())))
        .then((jsons: AreaMetaFile[]) => {
          countries.forEach((country, index) => {
            setMetaData((data) => ({ ...data, [country]: jsons[index] }));
          });
          setLoading(false);
        })
        .catch((error) => {
          setError(error);
          setLoading(false);
        });
    })();

    return () => {
      setMetaData({});
    };
  }, [active, countries]);

  const countryStateMap = useMemo(() => {
    return (Object.keys(metaData) as CountryCode[]).reduce((result, countryCode) => {
      const regions = metaData[countryCode]!.regions;
      regions.forEach((region) => {
        result[`${countryCode}_${region.id}`] = region.name;
      });
      return result;
    }, {} as Record<CountryState, string>);
  }, [metaData]);

  return [countryStateMap, loading, error];
};

export default useCountryStateDisplayNames;
